import { BaseSyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';

import useUpdateScheduledMessageMutation from 'components/domain/automation/scheduledMessages/useUpdateScheduledMessageMutation';
import { FormContextParts } from 'components/forms/form/Form.types';
import useNotify from 'hooks/useNotify';
import { BaseErrorResponse } from 'models/_base';
import { getErrorMessage } from 'utils/axiosExceptionUtils';
import { flushSync } from 'react-dom';
import { ScheduledMessagesEditModalFormValues } from './ScheduledMessagesEditModal.schema';

const useScheduledMessagesEditModalFormSubmit = ({
  scheduledMessageUid,
  closeModal,
}: {
  scheduledMessageUid: string;
  closeModal: () => void;
}) => {
  const { t } = useTranslation();
  const { notifyError, notifySuccess } = useNotify();

  const onError = (error: BaseErrorResponse) => {
    notifyError(
      getErrorMessage({
        apiErrorCode: error.response?.data?.apiErrorCode,
        apiErrorMessage: error.response?.data?.apiErrorMessage ?? '',
        baseKey: 'pageAutomations.triggers.apiErrorCodes.',
        t,
      }),
    );
  };

  const { mutateAsync: updateScheduledMessage } =
    useUpdateScheduledMessageMutation();

  const onSubmit = async (
    formValues: ScheduledMessagesEditModalFormValues,
    _event: BaseSyntheticEvent | undefined,
    { reset }: FormContextParts<ScheduledMessagesEditModalFormValues>,
  ) => {
    const { templateContent_htmlContent, templateSubject } = formValues;

    try {
      await updateScheduledMessage(
        {
          body: templateContent_htmlContent,
          subject: templateSubject,
          scheduledMessageUid,
        },
        {
          onSuccess: () => {
            // flushSync ensures the form is reset synchronously before
            // closeModal() triggers navigation. Without it, the dirty checker
            // would still see a dirty form during navigation and prompt the
            // user with an unsaved-changes confirmation.
            flushSync(() => reset(formValues, { keepDirty: false }));
            closeModal();
            notifySuccess(t('pageAutomations.scheduledMessages.modal.success'));
          },
        },
      );
    } catch (error) {
      onError(error as BaseErrorResponse);
    }
  };

  return { onSubmit };
};

export default useScheduledMessagesEditModalFormSubmit;
